// bankCheck.js
// The bank's 3D Secure check on /api/checkout-stripe (backend PR #461) and
// /api/upsell-checkout.
//
// A card whose bank wants to confirm the payment comes back as a 200 with
// `status: 'requires_action'`. /api/checkout-stripe sends one PaymentIntent client
// secret per subscription in `client_secrets`, and the backend activates the order
// from Stripe's webhook once they are confirmed: there is no second call to make.
// /api/upsell-checkout sends one in `client_secret`, which its callers pass as a
// one-item list, and wants the same body back once it passes
// (recordConfirmedUpsell). Shared by every checkout and upsell in this app.

export const needsBankCheck = (data) => !!data && data.status === 'requires_action';

/**
 * Stripe's developer words for a payment held at the bank check, passed on as
 * the backend's `message`: "…requires additional user action…" and
 * `authentication_required` from a backend without the 3D Secure change,
 * "Payment failed with status: requires_action" from an /api/upsell-checkout
 * without the bank check.
 * Never shopper copy.
 */
const BANK_CHECK_TEXT = /requires additional user action|authentication_required|requires_action/i;

/** The backend's message, or the caller's own line when it is Stripe's bank-check text. */
export const shopperMessage = (message, fallback) =>
    (typeof message === 'string' && message && !BANK_CHECK_TEXT.test(message) ? message : fallback);

// What a confirmed intent is left in. `requires_capture` is a prescription preauth.
const CONFIRMED = ['succeeded', 'processing', 'requires_capture'];

/* Stripe.js writes these two for the shopper ("We are unable to authenticate your
   payment method…"). Every other error type carries developer text. */
const shopperError = (error) =>
    !!error && (error.type === 'card_error' || error.code === 'payment_intent_authentication_failure');

/**
 * Shows the bank's check for each secret, one after another, and stops at the
 * first that does not go through.
 *
 * `stripe` must be on this app's publishable key: the intents belong to that
 * account. Any instance on it will do.
 *
 * Resolves `{ ok: true }`, or `{ ok: false, message, confirmed }`. `message` is
 * Stripe.js's own sentence for the shopper, or null when it sent none.
 * `confirmed` counts the secrets that went through before the failure: above
 * zero, part of the order was paid, so the caller must not invite a retry.
 */
export const runBankChecks = async (stripe, clientSecrets) => {
    const secrets = Array.isArray(clientSecrets) ? clientSecrets.filter(Boolean) : [];
    if (!stripe || !secrets.length) return { ok: false, message: null, confirmed: 0 };
    let confirmed = 0;
    for (const clientSecret of secrets) {
        const { paymentIntent, setupIntent, error } = await stripe.handleNextAction({ clientSecret });
        const intent = paymentIntent || setupIntent;
        if (error || !intent || !CONFIRMED.includes(intent.status)) {
            return { ok: false, message: shopperError(error) ? shopperMessage(error.message, null) : null, confirmed };
        }
        confirmed += 1;
    }
    return { ok: true };
};

/**
 * After the bank says yes on an /api/upsell-checkout: re-posts the same body plus
 * the confirmed PaymentIntent, which records the add-on. `post(body)` sends it
 * with a fresh X-Correlation-Id.
 *
 * Stripe already has the money, and the backend also records it by itself within
 * seconds, so this never fails the sale: an answer other than `success` or
 * `already_recorded` is only logged. With no id there is nothing to confirm, and
 * the same body without it would be a fresh charge, so nothing is sent.
 */
export const recordConfirmedUpsell = async (post, body, paymentIntentId) => {
    if (!paymentIntentId) {
        console.error('[upsell] no payment_intent_id to confirm: the backend records the add-on by itself.');
        return;
    }
    try {
        const { data } = await post({ ...body, confirmedPaymentIntentId: paymentIntentId });
        const recorded = !!data && (data.status === 'success' || data.already_recorded === true);
        if (!recorded) console.error('[upsell] the confirmed add-on was not recorded:', data);
    } catch (error) {
        console.error('[upsell] recording the confirmed add-on failed:', error);
    }
};
